// OAuth Utility Functions
// Helper functions for OAuth flow management

/**
 * Parse OAuth callback URL parameters
 * @param {string} url - Current URL or URL string
 * @returns {Object} Parsed URL parameters
 */
export function parseOAuthCallback(url = window.location.href) {
  const urlObj = new URL(url);
  const params = new URLSearchParams(urlObj.search);
  
  return {
    code: params.get('code'),
    state: params.get('state'),
    error: params.get('error'),
    error_description: params.get('error_description')
  };
}

/**
 * Generate random state parameter for OAuth security
 * @returns {string} Random state string
 */
export function generateOAuthState() {
  return Math.random().toString(36).substring(2, 15) + 
         Math.random().toString(36).substring(2, 15);
}

/**
 * Validate OAuth state parameter
 * @param {string} receivedState - State from OAuth callback
 * @param {string} expectedState - Expected state value
 * @returns {boolean} Whether state is valid
 */
export function validateOAuthState(receivedState, expectedState) {
  return receivedState === expectedState;
}

/**
 * Store OAuth state in session storage
 * @param {string} state - State value to store
 */
export function storeOAuthState(state) {
  sessionStorage.setItem('oauth_state', state);
}

/**
 * Retrieve OAuth state from session storage
 * @returns {string|null} Stored state value
 */
export function getOAuthState() {
  return sessionStorage.getItem('oauth_state');
}

/**
 * Clear OAuth state from session storage
 */
export function clearOAuthState() {
  sessionStorage.removeItem('oauth_state');
}

/**
 * Check if current URL is OAuth callback
 * @param {string} redirectUri - Expected redirect URI
 * @returns {boolean} Whether current URL is OAuth callback
 */
export function isOAuthCallback(redirectUri) {
  const currentUrl = window.location.href;
  return currentUrl.startsWith(redirectUri);
}

/**
 * Extract error information from OAuth callback
 * @param {Object} params - Parsed URL parameters
 * @returns {Object|null} Error information or null
 */
export function extractOAuthError(params) {
  if (params.error) {
    return {
      error: params.error,
      description: params.error_description || 'Unknown OAuth error',
      timestamp: new Date().toISOString()
    };
  }
  return null;
}

/**
 * Build OAuth redirect URL with parameters
 * @param {string} baseUrl - Base URL
 * @param {Object} params - Parameters to add
 * @returns {string} Complete redirect URL
 */
export function buildOAuthRedirectUrl(baseUrl, params = {}) {
  const url = new URL(baseUrl);
  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      url.searchParams.set(key, value);
    }
  });
  return url.toString();
}

/**
 * Clean up OAuth callback URL (remove parameters)
 * @param {string} url - URL to clean
 * @returns {string} Cleaned URL
 */
export function cleanOAuthCallbackUrl(url = window.location.href) {
  const urlObj = new URL(url);
  urlObj.search = '';
  return urlObj.toString();
}

/**
 * Check if OAuth tokens are expired
 * @param {Object} tokens - OAuth tokens
 * @returns {boolean} Whether tokens are expired
 */
export function areTokensExpired(tokens) {
  if (!tokens || !tokens.expiry_date) {
    return true;
  }
  
  const now = Date.now();
  const buffer = 5 * 60 * 1000; // 5 minutes buffer
  return tokens.expiry_date <= (now + buffer);
}

/**
 * Calculate token expiry time
 * @param {number} expiresIn - Token expiry time in seconds
 * @returns {number} Expiry timestamp in milliseconds
 */
export function calculateTokenExpiry(expiresIn) {
  return Date.now() + (expiresIn * 1000);
}

/**
 * Format OAuth error for display
 * @param {Object} error - OAuth error object
 * @returns {string} Formatted error message
 */
export function formatOAuthError(error) {
  if (!error) return 'Unknown OAuth error';
  
  const commonErrors = {
    'access_denied': 'Access was denied. Please try again.',
    'invalid_request': 'Invalid OAuth request. Please try again.',
    'invalid_client': 'OAuth client configuration error.',
    'invalid_grant': 'Invalid authorization code. Please try again.',
    'unsupported_response_type': 'Unsupported response type.',
    'invalid_scope': 'Invalid OAuth scope requested.',
    'server_error': 'OAuth server error. Please try again later.'
  };
  
  return commonErrors[error.error] || error.description || 'OAuth authentication failed';
}
